// Wire shapes the broker portal receives from api-gateway `/api/*`.
//
// Every interface mirrors a Go DTO in the contracts module field for
// field, with the JSON names exactly as the struct tags spell them:
//
//   contracts/httpapi/v1/broker_portal.go    portal bootstrap, hierarchy,
//                                            fees, revenue periods,
//                                            reconciliation, overview
//   contracts/httpapi/v1/broker.go           BrokerItem, staff, audit, accounts
//   contracts/httpapi/v1/broker_funding.go   pools, transfers, funding recon
//   contracts/httpapi/v1/broker_fees.go      fee plans, versions, assignments,
//                                            preview
//   contracts/httpapi/v1/broker_exposure.go  ExposureView
//   contracts/httpapi/v1/broker_revenue.go   share plans, earnings, balances,
//                                            payouts, statements, revenue recon
//   contracts/httpapi/v1/broker_clearing.go  BrokerClearingBalance
//   contracts/httpapi/v1/settlement.go       PositionItem, SettlementTradeItem
//   contracts/httpapi/v1/orders.go           OrderItem
//   contracts/httpapi/v1/client_log.go       login log, order client log
//   contracts/broker/v1/*.go                 the enums below
//
// AMOUNTS. Broker money is a CANONICAL 8dp integer string (1 GOLD =
// "100000000") of ONE currency. Positions and trades come from
// settlement-service and orders from order-service; broker-service relays
// their lists unchanged, so they keep those services' public 4dp wire
// (components/PositionsTable.tsx, OrdersTable.tsx, TradesTable.tsx,
// lib/units.ts). The portal never sums, converts or re-derives a figure:
// it renders what the server sent.
//
// Optional (`?`) fields are the Go `omitempty` ones. Slices the Go side
// does not mark omitempty can still arrive as null from a nil slice, so
// every reader goes through `?? []`.

// --- contracts/broker/v1 vocabulary -----------------------------------------

export type BrokerStatus = "DRAFT" | "ACTIVE" | "PAUSED" | "CLOSING" | "CLOSED";
export const BROKER_STATUSES: readonly BrokerStatus[] = ["DRAFT", "ACTIVE", "PAUSED", "CLOSING", "CLOSED"];

export type StaffRole = "OWNER" | "ADMINISTRATOR" | "FINANCE" | "TRADER" | "VIEWER";
/** contracts AllStaffRoles(): most to least privileged. */
export const STAFF_ROLES: readonly StaffRole[] = ["OWNER", "ADMINISTRATOR", "FINANCE", "TRADER", "VIEWER"];

export type StaffMembershipStatus = "INVITED" | "ACTIVE" | "SUSPENDED" | "REMOVED";

export type StaffCapability =
  | "VIEW"
  | "FINANCE_VIEW"
  | "STAFF_MANAGE"
  | "CHILD_CREATE"
  | "ACCOUNT_PAUSE"
  | "FUNDING_REQUEST"
  | "FEE_MANAGE"
  | "BRANDING_MANAGE";
/**
 * contracts AllStaffCapabilities(): the stable order (FEE_MANAGE appended
 * by SM-842, BRANDING_MANAGE by SM-978).
 */
export const STAFF_CAPABILITIES: readonly StaffCapability[] = [
  "VIEW",
  "FINANCE_VIEW",
  "STAFF_MANAGE",
  "CHILD_CREATE",
  "ACCOUNT_PAUSE",
  "FUNDING_REQUEST",
  "FEE_MANAGE",
  "BRANDING_MANAGE",
];

/**
 * SM-978: contracts/httpapi/v1 BrokerCurrencyImageResponse — one node's
 * GOLD image. Nullable fields arrive as null. effective = own ?? inherited;
 * null = the platform's Gold. inherited_from_* are null when the ancestor
 * is above every node the caller's memberships cover (the image still is
 * sent).
 */
export interface BrokerCurrencyImageResponse {
  currency: string;
  own_image_url: string | null;
  effective_image_url: string | null;
  inherited_image_url: string | null;
  inherited_from_broker_id: string | null;
  inherited_from_display_name: string | null;
  updated_at: string | null;
  updated_by: string | null;
  updated_by_kind: string | null;
  updated_by_name: string | null;
}

export type AccountStatus = "ACTIVE" | "SUSPENDED" | "CLOSED";
export const ACCOUNT_STATUSES: readonly AccountStatus[] = ["ACTIVE", "SUSPENDED", "CLOSED"];

export type BalanceMode = "SYSTEM_BROKER" | "BROKER_FUNDED" | "THIRD_PARTY_CREDIT";
export const BALANCE_MODES: readonly BalanceMode[] = ["SYSTEM_BROKER", "BROKER_FUNDED", "THIRD_PARTY_CREDIT"];

export type EntryPath = "REGISTRATION" | "SOCIAL_REGISTRATION" | "LOGIN_RECOVERY" | "PROVIDER_LAUNCH" | "BACKFILL";

export type AccountScope = "direct" | "subtree";

export type FundingRole = "MASTER" | "POOL";
export type FundingTransferKind = "POOL_ALLOCATION" | "POOL_RETURN" | "USER_ALLOCATION" | "USER_RETURN";
export const FUNDING_TRANSFER_KINDS: readonly FundingTransferKind[] = [
  "POOL_ALLOCATION",
  "POOL_RETURN",
  "USER_ALLOCATION",
  "USER_RETURN",
];
export type FundingTransferState = "PENDING" | "COMMITTED" | "FAILED" | "MANUAL_REVIEW";
export type FundingActorKind = "BROKER_CREDENTIAL" | "EXCHANGE_ADMIN" | "BROKER_STAFF";

export type EarningState = "PENDING" | "AVAILABLE" | "PAYOUT_RESERVED" | "PAID" | "VOID" | "REASSIGNED";
export const EARNING_STATES: readonly EarningState[] = [
  "PENDING",
  "AVAILABLE",
  "PAYOUT_RESERVED",
  "PAID",
  "VOID",
  "REASSIGNED",
];
export type RevenueRole = "MAKER" | "TAKER" | "REDEMPTION";
export const REVENUE_ROLES: readonly RevenueRole[] = ["MAKER", "TAKER", "REDEMPTION"];
export type RevenueSourceKind = "FILL" | "REDEMPTION";
export type EarningOrigin = "EARNED" | "ADJUSTMENT" | "REASSIGNMENT";
export type RevenueReconState = "RUNNING" | "MATCHED" | "MISMATCH" | "FAILED";

export type PayoutSchedule = "WEEKLY" | "MONTHLY" | "MANUAL";
export type PayoutBatchState =
  | "DRAFT"
  | "PENDING_REVIEW"
  | "APPROVED"
  | "PROCESSING"
  | "PAID"
  | "FAILED"
  | "REJECTED"
  | "CANCELLED";
/** contracts AllPayoutBatchStates(). */
export const PAYOUT_BATCH_STATES: readonly PayoutBatchState[] = [
  "DRAFT",
  "PENDING_REVIEW",
  "APPROVED",
  "PROCESSING",
  "PAID",
  "FAILED",
  "REJECTED",
  "CANCELLED",
];
export type PayoutApprovalDecision = "APPROVE" | "REJECT";
export type StatementScope = "node" | "subtree";

export type OverviewPanelState = "OK" | "EMPTY" | "UNAVAILABLE" | "FORBIDDEN";
export type AttentionKind =
  | "RECON_FINDINGS"
  | "FAILED_PAYOUTS"
  | "PAUSED_ACCOUNTS"
  | "PAUSED_NODES"
  | "PENDING_INVITATIONS"
  | "EXPOSURE_WARNING";
export const ATTENTION_KINDS: readonly AttentionKind[] = [
  "RECON_FINDINGS",
  "FAILED_PAYOUTS",
  "PAUSED_ACCOUNTS",
  "PAUSED_NODES",
  "PENDING_INVITATIONS",
  "EXPOSURE_WARNING",
];

export type ClearingDirection = "WE_OWE_BROKER" | "BROKER_OWES_US" | "FLAT";
export type ExposureBlockReason = "HEADROOM_EXHAUSTED" | "RECEIVABLE_OPEN" | "LIFECYCLE";
export type FeeAssignmentScope = "GROUP" | "BRANCH" | "NODE";
export type ObligationKind = string;
export type ClearingAction = string;

// --- pagination + errors ----------------------------------------------------

export interface PageInfo {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
}

export interface BlockingObligation {
  kind: ObligationKind;
  currency?: string;
  amount?: string;
  count?: number;
  subject_ids?: string[];
  clearing_action: ClearingAction;
}

/** BrokerErrorBody — the {"error":{...}} envelope (httpapi/v1 broker.go). */
export interface BrokerErrorBody {
  error: {
    code: string;
    message: string;
    details?: Record<string, string>;
    request_id?: string;
    blocking?: BlockingObligation[];
  };
}

// --- auth (api-gateway /api/auth/*) -----------------------------------------

export interface LoginResponse {
  user_id: string;
  email: string;
  role: string;
  csrf_token: string;
  expires_at: string;
}

export interface LoginFactorOption {
  factor_id: string;
  kind: string;
  friendly_name: string;
}

export interface LoginMFAChallenge {
  needs_mfa: true;
  challenge_id: string;
  user_id: string;
  email: string;
  factors: LoginFactorOption[];
}

export interface AuthMe {
  user_id: string;
  email: string;
  role: string;
  csrf_token?: string;
  expires_at?: string;
  /** Staff sign-in (SM-968): the staff account's own name and 2FA state. */
  display_name?: string;
  totp_enrolled?: boolean;
}

// --- broker staff identity (SM-968, /api/broker/auth/*) ----------------------

/** FF_BROKER_STAFF_IDENTITY: which sign-in the portal uses. */
export type StaffIdentityMode = "client" | "staff";

export type StaffAccountStatus = "PENDING" | "ACTIVE" | "SUSPENDED" | "CLOSED";

/** The signed-in staff account (GET /api/broker/auth/me). */
export interface StaffAccountItem {
  staff_id: string;
  email: string;
  display_name: string;
  status: StaffAccountStatus;
  email_verified: boolean;
  totp_enrolled: boolean;
  password_changed_at_unix_ms?: number;
  /** The session's CSRF token (the gateway adds it on GET me). */
  csrf_token?: string;
}

/** A staff session as the browser reads it (the gateway keeps the token in its cookie). */
export interface StaffSessionResponse {
  session_id: string;
  csrf_token: string;
  expires_at_unix_ms: number;
  staff: StaffAccountItem;
}

export interface StaffTOTPEnrollResponse {
  factor_id: string;
  secret: string;
  otpauth_url: string;
  digits: number;
  period: number;
}

// --- currency registry (GET /api/currencies) --------------------------------

export interface CurrencyInfo {
  code: string;
  display_name: string;
  symbol: string;
  image_url?: string;
  color?: string;
  display_precision: number;
  sort_order: number;
}

// --- organizations, staff, hierarchy (broker.go / broker_portal.go) ---------

/** Contacts and business_settings are JSON objects (json.RawMessage). */
export type JSONObject = Record<string, unknown>;

export interface BrokerItem {
  broker_id: string;
  parent_broker_id?: string;
  root_broker_id: string;
  broker_depth: number;
  status: BrokerStatus;
  status_reason?: string;
  legal_name: string;
  display_name: string;
  contacts?: JSONObject | null;
  payout_info?: JSONObject | null;
  business_settings?: JSONObject | null;
  settings_version: number;
  hierarchy_version: number;
  row_version: number;
  is_system?: boolean;
  created_by?: string;
  /** Who created_by is (SM-915); created_by is "" when the exchange created the node. */
  created_by_kind?: string;
  created_at_unix_ms: number;
  updated_at_unix_ms: number;
  status_changed_at_unix_ms: number;
  effective_status?: BrokerStatus;
  child_count?: number;
}

export interface BrokerListResponse {
  items: BrokerItem[];
  page: PageInfo;
}

export interface CreateBrokerRequest {
  parent_broker_id?: string;
  legal_name: string;
  display_name: string;
  contacts?: JSONObject;
  payout_info?: JSONObject;
  business_settings?: JSONObject;
  reason: string;
}

export interface StaffMembershipItem {
  membership_id: string;
  broker_id: string;
  user_id: string;
  role: StaffRole;
  status: StaffMembershipStatus;
  invited_by: string;
  /** Who invited_by is (SM-915); invited_by is "" when the exchange invited. */
  invited_by_kind?: string;
  invited_at_unix_ms: number;
  activated_at_unix_ms?: number;
  suspended_at_unix_ms?: number;
  removed_at_unix_ms?: number;
  row_version: number;
  /** The membership node's display name; filled on subtree lists (SM-852). */
  broker_display_name?: string;
  /** An ancestor node's staff placed this membership (SM-852). The ancestor is never named. */
  granted_by_ancestor?: boolean;
  /** The member's staff account (SM-968): show these, never the raw id. */
  staff_id?: string;
  email?: string;
  display_name?: string;
  account_status?: StaffAccountStatus;
  /** The inviting staff member's name when a staff member invited. */
  invited_by_name?: string;
}

/** GET /api/broker/{id}/staff query (SM-852 adds scope and user_id). */
export type StaffListParams = {
  /** direct (default): the node's own staff; subtree: the node's and every descendant's. */
  scope?: AccountScope;
  /** One user's memberships only: in subtree scope, the nodes that person reaches. */
  user_id?: string;
  include_removed?: boolean;
  offset?: number;
  limit?: number;
};

export interface StaffMembershipListResponse {
  items: StaffMembershipItem[];
  page: PageInfo;
}

/** Invite by email (SM-968): the server never takes an id here any more. */
export interface InviteStaffRequest {
  email: string;
  role: StaffRole;
  reason?: string;
}

export interface ChangeStaffRoleRequest {
  role: StaffRole;
  reason: string;
}

export interface StaffTransitionRequest {
  reason: string;
}

export interface BrokerAuditEventItem {
  event_id: string;
  broker_id: string;
  entity_kind: string;
  entity_id: string;
  action: string;
  actor_kind: string;
  actor_id: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
  request_id?: string;
  occurred_at_unix_ms: number;
  /** A staff actor's name (SM-968); empty for anyone else. */
  actor_name?: string;
}

export interface BrokerAuditListResponse {
  items: BrokerAuditEventItem[];
  page: PageInfo;
}

export interface StaffScopeItem {
  membership: StaffMembershipItem;
  broker: BrokerItem;
  is_root: boolean;
  descendant_count: number;
  capabilities: StaffCapability[];
}

export interface StaffMeResponse {
  user_id: string;
  /** The signed-in staff account (SM-968); absent on a customer session. */
  staff?: StaffAccountItem;
  memberships: StaffScopeItem[];
}

export interface BrokerBreadcrumbItem {
  broker_id: string;
  display_name: string;
  broker_depth: number;
  status: BrokerStatus;
  effective_status: BrokerStatus;
}

export interface StaffPathResponse {
  broker_id: string;
  scope_root_broker_id: string;
  path: BrokerBreadcrumbItem[];
  /**
   * The currencies broker_id itself trades, from its trading modes (CASH →
   * GOLD, GAME → RED + GREEN), computed by broker-service (SM-851). Only
   * the SYSTEM_BROKER trades in game mode (SM-866): every broker node reads
   * ["GOLD"]. Absent from a server that predates it, which the portal reads
   * as GOLD — or all three on the SYSTEM_BROKER (lib/currency
   * nodeCurrencies).
   */
  currencies?: string[];
}

// --- accounts (broker.go) ----------------------------------------------------

export interface BrokerAccountItem {
  broker_account_id: string;
  user_id: string;
  broker_id: string;
  root_broker_id: string;
  broker_path_snapshot_id: string;
  balance_mode: BalanceMode;
  status: AccountStatus;
  entry_path: EntryPath;
  referral?: unknown;
  consent?: unknown;
  status_reason?: string;
  row_version: number;
  created_at_unix_ms: number;
  updated_at_unix_ms: number;
  closed_at_unix_ms?: number;
  effective_status?: BrokerStatus;
}

export interface BrokerAccountListResponse {
  items: BrokerAccountItem[];
  page: PageInfo;
}

export interface BrokerAccountTransitionRequest {
  reason: string;
  expected_row_version?: number;
}

export interface BrokerAccountEventItem {
  event_id: string;
  broker_account_id: string;
  action: string;
  actor_kind: string;
  actor_id: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
  request_id?: string;
  occurred_at_unix_ms: number;
}

export interface BrokerAccountEventListResponse {
  items: BrokerAccountEventItem[];
  page: PageInfo;
}

// --- positions (settlement.go) ------------------------------------------------

export interface PositionItem {
  position_id: string;
  user_id: string;
  market_id: string;
  outcome_id?: string;
  outcome_code?: string;
  outcome_name?: string;
  direction: string;
  quantity: string;
  avg_price: string;
  realized_pnl: string;
  status: string;
  redeemable_payout?: string;
  winning_outcome_id?: string;
  amount_precision: number;
  opened_at: string;
  updated_at: string;
  trading_channel_id?: string;
  mode?: string;
  debit_currency?: string;
  payout_currency?: string;
  broker_account_id?: string;
  broker_id?: string;
  root_broker_id?: string;
  broker_path_snapshot_id?: string;
  balance_mode?: string;
}

export interface PositionListResponse {
  positions: PositionItem[];
  page: PageInfo;
}

// --- orders (orders.go) and trades (settlement.go), SM-823 ---------------------
//
// One account's or one node scope's orders and fills, as order-service and
// settlement-service list them. Prices are int64 minor units at precision
// 4 ($1 = "10000"); quantities are minor units at amount_precision; cash
// (notional, fee_amount, broker_fee_amount, net_settled) is the 4dp wire in
// the row's currency; times are RFC 3339 strings. side / price are BOOK
// space; intent_side / intent_price restate the row as the user placed it
// (a "Buy NO @ 75¢" books as SELL @ 2500).

export interface OrderItem {
  order_id: string;
  client_order_id?: string;
  market_id: string;
  user_id?: string;
  outcome_id?: string;
  outcome_code?: string;
  outcome_name?: string;
  side: string;
  price: string;
  intent_side?: string;
  intent_price?: string;
  quantity: string;
  filled_quantity: string;
  remaining_quantity: string;
  status: string;
  tif?: string;
  created_at: string;
  /** HISTORY view only: the terminal explanation and when the order reached it. */
  reason?: string;
  occurred_at?: string;
  expire_at?: string;
  amount_precision?: number;
  pricing_policy_version?: number;
  trading_channel_id?: string;
  mode?: string;
  debit_currency?: string;
  payout_currency?: string;
  currency_policy_version?: number;
  broker_account_id?: string;
  broker_id?: string;
  root_broker_id?: string;
  broker_path_snapshot_id?: string;
  balance_mode?: string;
  fee_plan_id?: string;
  fee_owner_broker_id?: string;
  revenue_share_plan_id?: string;
  funding_resolution?: string;
  broker_maker_fee_rate?: string;
  broker_taker_fee_rate?: string;
  broker_fee_rate_precision?: number;
}

export interface OrderListResponse {
  orders: OrderItem[];
  page: PageInfo;
}

/** One SIDE of a fill: a trade has two rows, so trade_record_id is the row key. */
export interface SettlementTradeItem {
  trade_id: string;
  order_id: string;
  user_id: string;
  market_id: string;
  outcome_id?: string;
  side: string;
  intent_side?: string;
  is_maker: boolean;
  price: string;
  intent_price?: string;
  quantity: string;
  notional: string;
  fee_rate: string;
  /** The exchange fee; the broker's commission is broker_fee_amount. */
  fee_amount: string;
  /** Signed cash leg: a buy's −(notional + fees), a sell's notional − fees. */
  net_settled: string;
  ledger_status: string;
  occurred_at: string;
  settled_at?: string;
  trading_channel_id?: string;
  mode?: string;
  /** The side's cash leg: CASH = GOLD, a GAME buy = RED, a GAME sell = GREEN. */
  currency?: string;
  broker_account_id?: string;
  broker_id?: string;
  root_broker_id?: string;
  broker_path_snapshot_id?: string;
  balance_mode?: string;
  fee_plan_id?: string;
  fee_owner_broker_id?: string;
  revenue_share_plan_id?: string;
  trade_record_id?: string;
  /** Absent when the fill had no broker component. */
  broker_fee_amount?: string;
  /**
   * Read from the position the fill produced (settlement_trades has no such
   * column), so absent means 0 OR unknown; the portal reads it as 0.
   */
  amount_precision?: number;
}

export interface SettlementTradeListResponse {
  trades: SettlementTradeItem[];
  page: PageInfo;
}

// --- login log and order client log (client_log.go, SM-822) ---------------------
//
// Times are RFC 3339 strings in UTC (not unix ms), as settlement's are.
// Every field below without "?" is always present; "" means nothing was
// recorded (shown as "—").

export type LoginMethod = "PASSWORD" | "MFA" | "GOOGLE" | "EMAIL_CONFIRM" | "PROVIDER_LAUNCH";

/** One login of the account's user inside the account's lifetime. */
export interface BrokerAccountLoginItem {
  /** client-service's event id (the row key). */
  event_id: number;
  /** "" when the event names no known method. */
  method: LoginMethod | "";
  ip: string;
  user_agent: string;
  occurred_at: string;
}

export interface BrokerAccountLoginListResponse {
  items: BrokerAccountLoginItem[];
  page: PageInfo;
}

export type OrderChannel = "WEB" | "EXTERNAL_LAUNCH" | "OPENAPI";

export interface OrderClientLogItem {
  order_id: string;
  user_id: string;
  broker_id: string;
  root_broker_id: string;
  broker_account_id: string;
  /** "" while the order's origin is unrecorded, as ip and user_agent. */
  channel: OrderChannel | "";
  ip: string;
  user_agent: string;
  placed_at: string;
  market_id?: string;
  outcome_id?: string;
  /** The intent side, BUY or SELL. */
  side?: string;
}

/** One keyset page, newest first: no total; next_cursor is absent on the last page. */
export interface OrderClientLogListResponse {
  items: OrderClientLogItem[];
  next_cursor?: string;
}

// --- exposure (broker_exposure.go) --------------------------------------------

export interface ExposureView {
  broker_id: string;
  root_broker_id: string;
  currency: string;
  effective_status?: string;
  own_exposure: string;
  subtree_exposure: string;
  open_entries: number;
  /** Σ open_entries over the node and every descendant (SM-874); absent from an older server. */
  subtree_open_entries?: number;
  collateral: string;
  has_own_limit: boolean;
  allowance?: string;
  allowance_expires_at_unix_ms?: number;
  limit?: string;
  headroom?: string;
  utilisation_bps?: number;
  warning_threshold_bps?: number;
  effective_headroom?: string;
  binding_broker_id?: string;
  warning: boolean;
  blocked: boolean;
  blocked_reasons?: ExposureBlockReason[];
  net_position?: string;
  owed?: string;
  covered_by_resolutions?: string;
  uncovered?: string;
  receivables_open: number;
  receivable_open_amount?: string;
  as_of_unix_ms: number;
}

// --- funding (broker_funding.go) ----------------------------------------------

export interface FundingPoolBalance {
  broker_id: string;
  root_broker_id: string;
  role: FundingRole;
  currency: string;
  account_code: string;
  wallet_id: string;
  status: string;
  available: string;
  reserved: string;
  pending_withdrawal: string;
  can_spend: boolean;
  can_receive: boolean;
}

export interface FundingPoolListResponse {
  broker_id: string;
  root_broker_id: string;
  pools: FundingPoolBalance[];
  as_of_unix_ms: number;
}

export interface PoolTransferRequest {
  source_broker_id?: string;
  destination_broker_id: string;
  currency: string;
  amount: string;
  external_reference?: string;
  reason?: string;
}

export interface FundingTransferItem {
  transfer_id: string;
  kind: FundingTransferKind;
  state: FundingTransferState;
  source_broker_id?: string;
  destination_broker_id?: string;
  broker_account_id?: string;
  currency: string;
  amount: string;
  operation_id: string;
  external_reference?: string;
  actor_kind: FundingActorKind;
  actor_id: string;
  ledger_transfer_id?: string;
  failure_code?: string;
  attempts: number;
  created_at_unix_ms: number;
  settled_at_unix_ms?: number;
  replayed?: boolean;
}

export interface FundingTransferListResponse {
  items: FundingTransferItem[];
  next_cursor?: string;
}

export interface FundingReconRootSummary {
  root_broker_id: string;
  currency: string;
  top_ups: string;
  payouts_completed: string;
  payouts_open: string;
  node_total: string;
  user_allocations: string;
  user_returns: string;
  nodes: number;
  balanced: boolean;
}

export interface FundingReconFinding {
  rule: string;
  severity: string;
  root_broker_id?: string;
  broker_id?: string;
  currency?: string;
  operation_id?: string;
  transfer_id?: string;
  expected?: string;
  actual?: string;
  detail: string;
  /** Names detail's sentence (contracts brokerv1 ReconDetail*, SM-941); absent on a run written before codes. */
  detail_code?: string;
}

export interface FundingReconRunItem {
  run_id: string;
  trigger: string;
  status: string;
  started_at_unix_ms: number;
  finished_at_unix_ms?: number;
  roots: FundingReconRootSummary[];
  findings: FundingReconFinding[];
  error?: string;
}

// --- clearing (broker_clearing.go) --------------------------------------------

export interface BrokerClearingBalance {
  broker_id: string;
  root_broker_id: string;
  currency: string;
  account_code: string;
  wallet_id?: string;
  exists: boolean;
  balance: string;
  direction: ClearingDirection;
  owed: string;
  period_delta?: string;
  period_legs?: number;
  from_unix_ms?: number;
  to_unix_ms?: number;
  root_limit?: string;
  root_headroom?: string;
  headroom_utilisation_bps?: number;
  as_of_unix_ms: number;
}

// --- fees (broker_fees.go + broker_portal.go) ----------------------------------
//
// Authoring (SM-835): the root's OWNER and FINANCE (FEE_MANAGE) call
// /api/broker/{root_id}/fee-plans* and /fee-assignments with exactly the
// operator surface's bodies. Rates are integer strings at precision 6
// ("10000" = 1 %, 0..1000000); times are unix ms, 0 or absent = now (a past
// time is refused); every write carries a reason.

export interface FeePlanVersionItem {
  plan_version_id: string;
  plan_id: string;
  root_broker_id: string;
  version: number;
  currency: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  redemption_fee_rate: string;
  fee_rate_precision: number;
  effective_at_unix_ms: number;
  published_by: string;
  /** Who published_by is (SM-915); published_by is "" when the exchange published. */
  published_by_kind?: string;
  published_at_unix_ms: number;
}

export interface FeePlanItem {
  plan_id: string;
  root_broker_id: string;
  name: string;
  protected?: boolean;
  retired_at_unix_ms?: number;
  created_by: string;
  /** Who created_by is (SM-915); created_by is "" when the exchange created the plan. */
  created_by_kind?: string;
  created_at_unix_ms: number;
  versions?: FeePlanVersionItem[];
  /**
   * The plan detail only: the union of the currencies the root and its
   * non-CLOSED descendants trade — the only currencies a new version may be
   * published in (BROKER_FEE_CURRENCY_NOT_TRADED otherwise, SM-851). Every
   * broker tree reads ["GOLD"] since SM-866; missing or empty means GOLD,
   * or all three on the SYSTEM_BROKER (lib/feeAuthoring
   * publishableCurrencies).
   */
  tree_currencies?: string[];
}

/** A page of a root's plans (versions omitted). */
export interface FeePlanListResponse {
  items: FeePlanItem[];
  page: PageInfo;
}

/** An empty plan under the root; rates come with its first version. */
export interface CreateFeePlanRequest {
  name: string;
  reason: string;
}

/**
 * The plan's next version for ONE currency. A RED or GREEN version carries
 * redemption_fee_rate "0" (redemption fees are GOLD only).
 */
export interface PublishFeePlanVersionRequest {
  currency: string;
  maker_fee_rate: string;
  taker_fee_rate: string;
  redemption_fee_rate: string;
  effective_at_unix_ms?: number;
  reason: string;
}

/** Retire now (absent) or at a future time, once. */
export interface RetireFeePlanRequest {
  retire_at_unix_ms?: number;
  reason: string;
}

/** One immutable assignment; no plan_id = CLEARED (from its effective time the scope has no rule). */
export interface FeeAssignmentItem {
  assignment_id: string;
  root_broker_id: string;
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms: number;
  reason: string;
  published_by: string;
  /** Who published_by is (SM-915); published_by is "" when the exchange published. */
  published_by_kind?: string;
  published_at_unix_ms: number;
}

/** A page of the root's assignment history, newest effective first. */
export interface FeeAssignmentListResponse {
  items: FeeAssignmentItem[];
  page: PageInfo;
}

/**
 * Attach a root-owned plan at a scope, or (plan_id absent) clear the
 * scope's rule. For GROUP the target is the root. The same (scope, target,
 * effective time) twice is refused (BROKER_FEE_ASSIGNMENT_CONFLICT).
 */
export interface PublishFeeAssignmentRequest {
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms?: number;
  reason: string;
}

/**
 * Why no plan resolves (StaffFeeScheduleResponse.missing_rule, SM-837): the
 * details.rule an order here would be refused with
 * (BROKER_FEE_ASSIGNMENT_MISSING).
 */
export type FeeMissingRule = "NO_ASSIGNMENT" | "PLAN_RETIRED" | "NO_VERSION_IN_CURRENCY" | "PLAN_UNKNOWN" | "PLAN_OWNER_MISMATCH";

export interface StaffFeeScheduleResponse {
  broker_id: string;
  root_broker_id: string;
  currency: string;
  assignment?: FeeAssignmentItem | null;
  plan?: FeePlanItem | null;
  current?: FeePlanVersionItem | null;
  upcoming: FeePlanVersionItem[];
  upcoming_assignments: FeeAssignmentItem[];
  revenue_share_plan_version_id?: string;
  node_share_bps: number;
  resolved_at_unix_ms: number;
  /** Set only when no plan resolves (assignment / plan / current absent). Older servers omit it. */
  missing_rule?: FeeMissingRule | string;
}

export interface FeePreviewRequest {
  broker_id: string;
  currency: string;
  price: string;
  quantity: string;
  price_precision?: number;
  amount_precision?: number;
  market_id?: string;
  at_unix_ms?: number;
  share_broker_id?: string;
}

export interface FeePreviewComponent {
  maker_fee_rate: string;
  taker_fee_rate: string;
  fee_rate_precision: number;
  maker_fee: string;
  taker_fee: string;
  version?: string;
}

export interface FeePreviewNodeShare {
  broker_id: string;
  share_bps: number;
  maker_amount: string;
  taker_amount: string;
  revenue_share_plan_version_id: string;
}

export interface FeePreviewResponse {
  currency: string;
  notional: string;
  broker: FeePreviewComponent;
  exchange?: FeePreviewComponent | null;
  total_maker_fee: string;
  total_taker_fee: string;
  fee_plan_version_id: string;
  fee_owner_broker_id: string;
  fee_base?: string;
  node_share?: FeePreviewNodeShare | null;
}

export interface RedemptionPreviewRequest {
  broker_id: string;
  share_broker_id?: string;
  currency: string;
  quantity: string;
  amount_precision?: number;
  market_id?: string;
  at_unix_ms?: number;
}

export interface RedemptionNodeShare {
  broker_id: string;
  share_bps: number;
  amount: string;
  revenue_share_plan_version_id: string;
}

export interface RedemptionPreviewResponse {
  currency: string;
  gross: string;
  exchange_fee_rate?: string;
  exchange_fee_rate_precision?: number;
  exchange_fee?: string;
  exchange_version?: string;
  broker_fee_rate: string;
  broker_fee_rate_precision: number;
  broker_fee: string;
  total_fee: string;
  net_payout: string;
  fee_plan_version_id: string;
  fee_owner_broker_id: string;
  node_share?: RedemptionNodeShare | null;
}

// --- revenue-share plans (broker_revenue.go; authoring SM-846) ----------------
//
// The split of a broker commission among the nodes of the servicing path.
// A plan is owned by the ROOT; each version is immutable: shares in basis
// points (1..10000 each, total exactly 10000, no recipient twice) and a
// remainder recipient that holds a share. A recipient is a broker id of
// the root's tree or "@servicing" (the node that serviced the order). The
// root's OWNER and FINANCE (FEE_MANAGE) call /api/broker/{root_id}/
// revenue-share-plans* and /revenue-share-assignments with exactly the
// operator surface's bodies.

/** revshare's token for "the servicing broker of the order". */
export const SERVICING_RECIPIENT = "@servicing";
/** The built-in version every order locks when no share rule applies: 100 % to the servicing broker. */
export const DEFAULT_REVENUE_SHARE_VERSION_ID = "rsv_default_servicing_100";

export interface RevenueShare {
  recipient: string;
  share_bps: number;
}

export interface RevenueSharePlanVersionItem {
  plan_version_id: string;
  plan_id: string;
  /** "" for the built-in default. */
  root_broker_id?: string;
  version: number;
  shares: RevenueShare[];
  remainder_recipient: string;
  effective_at_unix_ms: number;
  published_by: string;
  /** Who published_by is (SM-915); published_by is "" when the exchange published. */
  published_by_kind?: string;
  published_at_unix_ms: number;
  reason?: string;
  /** Marks the built-in default (never stored). */
  default?: boolean;
}

export interface RevenueSharePlanItem {
  plan_id: string;
  root_broker_id: string;
  name: string;
  retired_at_unix_ms?: number;
  created_by: string;
  /** Who created_by is (SM-915); created_by is "" when the exchange created the plan. */
  created_by_kind?: string;
  created_at_unix_ms: number;
  /** The version effective now (absent when none yet). */
  current_version_id?: string;
}

export interface RevenueSharePlanListResponse {
  items: RevenueSharePlanItem[];
  page: PageInfo;
}

/** One plan with every version, newest first. */
export interface RevenueSharePlanDetail {
  plan: RevenueSharePlanItem;
  versions: RevenueSharePlanVersionItem[];
}

export interface CreateRevenueSharePlanRequest {
  name: string;
  reason: string;
}

export interface PublishRevenueShareVersionRequest {
  shares: RevenueShare[];
  remainder_recipient: string;
  effective_at_unix_ms?: number;
  reason: string;
}

export interface RetireRevenueSharePlanRequest {
  retire_at_unix_ms?: number;
  reason: string;
}

/** One immutable share assignment; no plan_id = cleared (the walk goes on from its effective time). */
export interface RevenueShareAssignmentItem {
  assignment_id: string;
  root_broker_id: string;
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms: number;
  reason?: string;
  published_by: string;
  /** Who published_by is (SM-915); published_by is "" when the exchange published. */
  published_by_kind?: string;
  published_at_unix_ms: number;
}

export interface RevenueShareAssignmentListResponse {
  items: RevenueShareAssignmentItem[];
  page: PageInfo;
}

export interface PublishRevenueShareAssignmentRequest {
  scope: FeeAssignmentScope;
  target_broker_id: string;
  plan_id?: string;
  effective_at_unix_ms?: number;
  reason: string;
}

// --- revenue (broker_revenue.go + broker_portal.go) ---------------------------

export interface RevenueByRole {
  maker: string;
  taker: string;
  redemption: string;
}

export interface RevenueBalance {
  broker_id: string;
  currency: string;
  pending: string;
  held: string;
  available: string;
  payout_reserved: string;
  paid: string;
  void: string;
  unpaid: string;
}

export interface RevenueBalancesResponse {
  broker_id: string;
  balances: RevenueBalance[];
}

export interface EarningItem {
  earning_id: string;
  recipient_broker_id: string;
  root_broker_id: string;
  origin: EarningOrigin;
  source_kind?: RevenueSourceKind;
  source_id?: string;
  role?: RevenueRole;
  currency: string;
  commission_total?: string;
  share_bps?: number;
  amount: string;
  state: EarningState;
  held: boolean;
  hold_reason?: string;
  release_id?: string;
  batch_id?: string;
  adjustment_id?: string;
  reassignment_id?: string;
  ledger_leg_id?: string;
  recipient_path: string[];
  created_at_unix_ms: number;
  released_at_unix_ms?: number;
  reserved_at_unix_ms?: number;
  paid_at_unix_ms?: number;
  reassigned_at_unix_ms?: number;
}

export interface EarningListResponse {
  items: EarningItem[];
  page: PageInfo;
}

export interface RevenuePeriodFigures {
  from_unix_ms: number;
  to_unix_ms: number;
  earned: RevenueByRole;
  shares_received: RevenueByRole;
  total: string;
}

export interface RevenuePeriodsResponse {
  broker_id: string;
  currency: string;
  today: RevenuePeriodFigures;
  current: RevenuePeriodFigures;
  previous: RevenuePeriodFigures;
  latest_recon_run_id?: string;
  latest_recon_state?: RevenueReconState;
  latest_recon_at_unix_ms?: number;
  as_of_unix_ms: number;
}

export interface RevenueReconItem {
  broker_id: string;
  currency: string;
  ledger_pending: string;
  earnings_pending: string;
  ledger_available: string;
  earnings_available: string;
  matched: boolean;
  in_flight?: boolean;
}

export interface RevenueReconEscrowItem {
  currency: string;
  ledger_escrow: string;
  batches_reserved: string;
  matched: boolean;
  in_flight?: boolean;
}

export interface RevenueReconRunItem {
  run_id: string;
  state: RevenueReconState;
  as_of_unix_ms: number;
  triggered_by: string;
  /** Who triggered_by is (SM-915): the scheduler, or the exchange (triggered_by ""). */
  triggered_by_kind?: string;
  started_at_unix_ms: number;
  finished_at_unix_ms?: number;
  items: RevenueReconItem[];
  escrow: RevenueReconEscrowItem[];
  mismatches: number;
  error?: string;
}

// --- payouts --------------------------------------------------------------------

export interface PayoutApprovalItem {
  approver_id: string;
  decision: PayoutApprovalDecision;
  reason?: string;
  decided_at_unix_ms: number;
}

export interface PayoutBatchItem {
  batch_id: string;
  broker_id: string;
  root_broker_id: string;
  currency: string;
  schedule: PayoutSchedule;
  period_from_unix_ms?: number;
  cutoff_unix_ms: number;
  /** A pre-SM-881 scheduled batch's minimum; omitted when zero, as every admin-prepared batch is (SM-912). */
  minimum?: string;
  total: string;
  item_count: number;
  state: PayoutBatchState;
  requested_by: string;
  request_reason?: string;
  approvals_required: number;
  approvals: PayoutApprovalItem[];
  executed_by?: string;
  finalized_by?: string;
  external_reference?: string;
  rail_reference?: string;
  failure_reason?: string;
  escrow_operation_id: string;
  restore_operation_id?: string;
  paid_operation_id?: string;
  escrow_posted: boolean;
  restore_posted?: boolean;
  paid_posted?: boolean;
  attempts?: number;
  last_error?: string;
  created_at_unix_ms: number;
  reviewed_at_unix_ms?: number;
  executed_at_unix_ms?: number;
  finalized_at_unix_ms?: number;
}

export interface PayoutBatchListResponse {
  items: PayoutBatchItem[];
  page: PageInfo;
}

// --- statements -------------------------------------------------------------------

export interface RevenueStatementSection {
  currency: string;
  opening: string;
  earned: RevenueByRole;
  shares_received: RevenueByRole;
  adjustments: string;
  reassigned_in: string;
  reassigned_out: string;
  payouts: string;
  closing: string;
  pending: string;
  held: string;
  available: string;
  payout_reserved: string;
  void_commission: string;
  traded_value: string;
  exchange_fee: string;
  broker_commission: string;
}

export interface RevenueStatementRow {
  row_id: string;
  kind: string;
  origin?: EarningOrigin;
  currency: string;
  occurred_at_unix_ms: number;
  recipient_broker_id: string;
  state: string;
  amount: string;
  share_bps?: number;
  source_kind?: RevenueSourceKind;
  source_id?: string;
  role?: RevenueRole;
  market_id?: string;
  trade_id?: string;
  order_id?: string;
  redemption_id?: string;
  servicing_broker_id?: string;
  traded_value?: string;
  total_user_fee?: string;
  exchange_fee?: string;
  broker_commission?: string;
  ledger_operation_id?: string;
  reference?: string;
  destination_id?: string;
}

export interface RevenueStatement {
  broker_id: string;
  scope: StatementScope;
  from_unix_ms: number;
  to_unix_ms: number;
  currency?: string;
  sections: RevenueStatementSection[];
  rows: RevenueStatementRow[];
  page: PageInfo;
  generated_at_unix_ms: number;
  latest_recon_run_id?: string;
  latest_recon_state?: string;
}

// --- reconciliation ----------------------------------------------------------------

export interface StaffReconciliationResponse {
  broker_id: string;
  funding_run?: FundingReconRunItem | null;
  revenue_run?: RevenueReconRunItem | null;
  cases_available: boolean;
  as_of_unix_ms: number;
}

// --- overview (SM-785) ----------------------------------------------------------------

export interface OverviewPanelMeta {
  state: OverviewPanelState;
  error_code?: string;
  as_of_unix_ms?: number;
}

/**
 * Fee totals (maker + taker + redemption, earned plus shares received) per
 * window; the per-role split is the Revenue page's (SM-861).
 */
export interface OverviewEarningsFigures {
  today: string;
  period: string;
  previous: string;
  all_time: string;
}

export interface OverviewEarningsPanel extends OverviewPanelMeta {
  period_from_unix_ms?: number;
  period_to_unix_ms?: number;
  previous_from_unix_ms?: number;
  own?: OverviewEarningsFigures | null;
  descendants?: OverviewEarningsFigures | null;
  /** The server's own + descendants; present only with descendants (SM-897). */
  total?: OverviewEarningsFigures | null;
}

export interface OverviewRevenueFigures {
  pending: string;
  held: string;
  available: string;
  payout_reserved: string;
}

export interface OverviewRevenuePanel extends OverviewPanelMeta {
  own?: OverviewRevenueFigures | null;
  descendants?: OverviewRevenueFigures | null;
  /** The server's own + descendants; present only with descendants (SM-897). */
  total?: OverviewRevenueFigures | null;
}

export interface OverviewFundingFigures {
  pool_available: string;
  pool_reserved: string;
  allocated_to_users: string;
  /**
   * Always set on own and descendants; absent on total, which has no
   * returnable figure (each descendant returns to its own parent; SM-897).
   */
  returnable?: string;
}

export interface OverviewFundingPanel extends OverviewPanelMeta {
  own?: OverviewFundingFigures | null;
  descendants?: OverviewFundingFigures | null;
  /** The server's own + descendants, without returnable; present only with descendants (SM-897). */
  total?: OverviewFundingFigures | null;
}

export interface OverviewExposurePanel extends OverviewPanelMeta {
  own_exposure?: string;
  subtree_exposure?: string;
  has_limit?: boolean;
  limit?: string;
  headroom?: string;
  utilisation_bps?: number;
  warning_threshold_bps?: number;
  warning?: boolean;
  blocked?: boolean;
}

export interface OverviewActivityFigures {
  active_accounts: number;
  open_orders: number;
  open_positions: number;
  fills: number;
  traded_value: string;
}

export interface OverviewActivityPanel extends OverviewPanelMeta {
  period_from_unix_ms?: number;
  period_to_unix_ms?: number;
  own?: OverviewActivityFigures | null;
  descendants?: OverviewActivityFigures | null;
  /**
   * The server's own + descendants; present only with descendants (so not
   * over 500 nodes). unavailable_figures applies to it too (SM-897).
   */
  total?: OverviewActivityFigures | null;
  /**
   * Figures (OverviewActivityFigures json names) a source could not read
   * while the panel stayed OK: they carry 0 on the wire and are shown as
   * unavailable, never as 0.
   */
  unavailable_figures?: string[];
}

export interface OverviewAttentionItem {
  kind: AttentionKind;
  count: number;
  broker_id?: string;
}

export interface OverviewAttentionPanel extends OverviewPanelMeta {
  items: OverviewAttentionItem[];
}

export interface BrokerOverviewResponse {
  broker_id: string;
  currency: string;
  has_descendants: boolean;
  descendant_count: number;
  earnings: OverviewEarningsPanel;
  revenue: OverviewRevenuePanel;
  funding: OverviewFundingPanel;
  exposure: OverviewExposurePanel;
  activity: OverviewActivityPanel;
  attention: OverviewAttentionPanel;
  generated_at_unix_ms: number;
}

// --- settlement position and out-of-band settlement notes (SM-621 phase
// 09a — SM-796 / SM-798 / SM-799; contracts httpapi/v1/broker_settlement.go
// and broker/v1/settlement.go). Canonical 8dp integer strings of ONE
// currency, signed where the figure is signed: positive = we owe the
// broker, negative = the broker owes us (B12). broker-service computes
// every figure; the portal only renders them. ---

export type SettlementPositionDirection = "WE_OWE_BROKER" | "BROKER_OWES_US" | "SETTLED";
export type SettlementNoteDirection = "INBOUND" | "OUTBOUND";
export type SettlementNoteStatus = "PENDING" | "CONFIRMED";
export type SettlementScope = "node" | "subtree";

export interface SettlementPositionFigures {
  revenue_released: string;
  revenue_paid: string;
  clearing_delta: string;
  clearing_delta_direction: SettlementPositionDirection;
  period_net: string;
  period_direction: SettlementPositionDirection;
  revenue_available: string;
  clearing_balance: string;
  clearing_balance_direction: SettlementPositionDirection;
  gross: string;
  gross_direction: SettlementPositionDirection;
  confirmed_inbound: string;
  confirmed_outbound: string;
  pending_inbound: string;
  pending_outbound: string;
  outstanding: string;
  direction: SettlementPositionDirection;
  outstanding_if_pending_clear: string;
  if_pending_clear_direction: SettlementPositionDirection;
  collateral: string;
  uncovered: string;
  transfer_in_required: string;
  note_count: number;
  pending_note_count: number;
  has_activity: boolean;
  clearing_account: boolean;
  // SM-884, display only — never inside another figure; absent from an
  // older server. Commission reserved by open payout batches.
  revenue_in_payout?: string;
  /** clearing + confirmed INBOUND − confirmed OUTBOUND notes, signed (B12): what a payment request asks for. */
  external_credit_outstanding?: string;
  external_credit_direction?: SettlementPositionDirection;
  payment_requests_open?: SettlementPaymentRequestsOpen;
  // SM-885 (PO-confirmed 2026-10-09): THIRD_PARTY_CREDIT only, parts of clearing_balance
  // (settled = clearing_balance + open cost); absent = not applicable,
  // a past period or the peer down. Rendered only when present.
  users_open_position_cost?: string;
  /**
   * Sent with users_open_position_cost (SM-912): the cost always counts
   * toward "the broker owes us" (clearing_balance = users_settled_result −
   * users_open_position_cost), so BROKER_OWES_US above 0, SETTLED at 0.
   */
  users_open_position_direction?: SettlementPositionDirection;
  users_settled_result?: string;
  users_settled_result_direction?: SettlementPositionDirection;
}

/** REQUESTED payment requests: how many, and Σ outstanding_amount (SM-884). */
export interface SettlementPaymentRequestsOpen {
  count: number;
  outstanding_amount: string;
}

/** A root's unresolved receivable (OPEN or ACKNOWLEDGED, SM-884); children read null. */
export interface SettlementReceivableRef {
  receivable_id: string;
  amount: string;
  state: string;
}

export interface SettlementFeeFigures {
  traded_value: string;
  exchange_fee: string;
  broker_commission: string;
}

export interface SettlementPositionBranch {
  broker_id: string;
  display_name: string;
  broker_depth: number;
  status: string;
  has_descendants: boolean;
  figures: SettlementPositionFigures;
}

export interface SettlementPositionResponse {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  display_name: string;
  broker_depth: number;
  status: string;
  currency: string;
  scope: SettlementScope;
  from_unix_ms: number;
  to_unix_ms: number;
  own: SettlementPositionFigures;
  consolidated?: SettlementPositionFigures;
  branches?: SettlementPositionBranch[];
  fees: SettlementFeeFigures;
  outstanding_caption: string;
  note_caption: string;
  /** Roots only; null on a child or when none is unresolved (SM-884). */
  open_receivable?: SettlementReceivableRef | null;
}

export interface SettlementPositionListItem {
  broker_id: string;
  root_broker_id: string;
  parent_broker_id?: string;
  display_name: string;
  broker_depth: number;
  status: string;
  has_descendants: boolean;
  figures: SettlementPositionFigures;
}

export interface SettlementPositionListResponse {
  currency: string;
  scope: SettlementScope;
  from_unix_ms: number;
  to_unix_ms: number;
  sort: string;
  items: SettlementPositionListItem[];
  page: PageInfo;
  outstanding_caption: string;
}

export interface SettlementNoteItem {
  note_id: string;
  broker_id: string;
  direction: SettlementNoteDirection;
  amount: string;
  currency: string;
  status: SettlementNoteStatus;
  value_date: string;
  external_reference: string;
  note: string;
  supersedes_note_id?: string;
  superseded_by_note_id?: string;
  author_id?: string;
  created_at_unix_ms: number;
  confirmed_by_id?: string;
  confirmed_at_unix_ms?: number;
  /** An INBOUND note linked to a payment request (SM-883). */
  payment_request_id?: string;
  captions: string[];
}

export interface SettlementNoteListResponse {
  items: SettlementNoteItem[];
  page: PageInfo;
}

// --- payment requests (SM-883; contracts httpapi/v1/broker_payment_requests.go,
// broker/v1 PaymentRequestStatus). The exchange asks a broker for the
// external credit it owes — never the net — and the broker pays outside
// Smart Market; linked CONFIRMED INBOUND notes make it PAID. The staff
// surface carries no actor ids (requested_by_id, cancelled_by_id). ---

export type PaymentRequestStatus = "REQUESTED" | "PAID" | "CANCELLED";
/** contracts AllPaymentRequestStatuses(). */
export const PAYMENT_REQUEST_STATUSES: readonly PaymentRequestStatus[] = ["REQUESTED", "PAID", "CANCELLED"];

export interface PaymentRequestItem {
  request_id: string;
  broker_id: string;
  broker_display_name: string;
  currency: string;
  amount: string;
  received_amount: string;
  outstanding_amount: string;
  /** YYYY-MM-DD, a calendar day in Asia/Bangkok. */
  due_date?: string;
  reference?: string;
  note?: string;
  status: PaymentRequestStatus;
  cancel_reason?: string;
  created_at_unix_ms: number;
  paid_at_unix_ms?: number;
  captions?: string[];
}

export interface PaymentRequestListResponse {
  items: PaymentRequestItem[];
  page: PageInfo;
}
